import { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid } from '@vapor/material'
import { Controller } from 'react-hook-form'
import DocumentCustomerInput from 'src/app/components/document-customer-input/document-customer-input'
import { HICTitleSmall } from 'src/app/components/typography/title-small'
import FormLayoutDrawer from 'src/app/layout/form-layout/form-layout-drawer'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import useUnpaidDocumentsReminderForm, { UnpaidDocumentsReminderFormSchema } from './unpaid-documents-reminder-form.hook'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { useTranslation } from 'react-i18next'
import { DocumentCustomerFormSchema } from 'src/app/components/document-customer-input/document-customer-schema'
import { ReferenceCustomerFragment, useUnpaidDocumentsQuery } from 'src/api/api.generated'
import { UnpaidDocumentsSelectionGrid } from './unpaid-documents-selection-grid'
import { HICError } from 'src/app/components/typography/error-text'
import { useSendReminderEmailForUnpaidDocumentsMutation } from 'src/api/api.generated'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { useConfirm } from 'hic-components'
import CustomerDetail from 'src/app/pages/common/customer/customer-detail'

type UnpaidReminderFormProps = {
  open: boolean
  tenantId: string
  propertyId: string
  onClose: () => void
  defaultCustomer?: DocumentCustomerFormSchema
  comeFromUnpaidDocumentId?: string
  referenceCustomer: ReferenceCustomerFragment
}

export default function UnpaidDocumentsReminderForm(props: UnpaidReminderFormProps) {
  const { open, tenantId, propertyId, onClose, defaultCustomer, comeFromUnpaidDocumentId, referenceCustomer } = props
  const { t } = useTranslation()
  const { md } = useBreakpoints()
  const showSuccessSnackBar = useSuccessSnackBar()
  const showError = useErrorSnackBar()
  const confirm = useConfirm()
  const [openEditCustomer, setOpenEditCustomer] = useState(false)

  const [localCustomerId, setLocalCustomerId] = useState(defaultCustomer?.id)

  const {
    data: unpaidDocuments,
    refetch: unpaidDocumentsRefetch,
    loading: unpaidDocumentsLoading,
  } = useUnpaidDocumentsQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        customerId: { eq: props.defaultCustomer?.id },
        unpaid: true,
      },
    },
    skip: !props.defaultCustomer,
  })

  useEffect(() => {
    unpaidDocumentsRefetch({
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        customerId: { eq: localCustomerId || null },
        unpaid: true,
      },
    })
  }, [localCustomerId])

  const [sendEmail, { loading: sendEmailLoading }] = useSendReminderEmailForUnpaidDocumentsMutation()

  const onSubmitHandler: (data: UnpaidDocumentsReminderFormSchema) => Promise<void> = useCallback(
    async (data) => {
      if (!data.customer.email) {
        return confirm({
          message: t('pms.accounting.documents.reminder.dialog.message'),
          title: t('pms.accounting.documents.reminder.dialog.title'),
          confirmButtonText: t('pms.accounting.documents.reminder.dialog.confirm'),
          cancelButtonText: t('pms.accounting.documents.reminder.dialog.cancel'),
          onConfirm: () => {
            setOpenEditCustomer(true)
          },
          onCancel: () => {
            return
          },
        })
      }
      sendEmail({
        variables: {
          input: {
            tenantId: tenantId,
            propertyId: propertyId,
            customerId: data.customer.id,
            unpaidDocumentIds: data.unpaidDocumentIds,
          },
        },
        onCompleted: (res) => {
          if (res.sendReminderEmailForUnpaidDocuments?.emailState === 'SEND') {
            showSuccessSnackBar(t('commons.email_sent'))
            onClose()
          }
        },
        onError: (error) => {
          showError(error.message)
        },
      })
    },
    [sendEmail, tenantId, propertyId],
  )

  const unpaidDocumentIds = useMemo(() => unpaidDocuments?.documents?.map((d) => d.id) ?? [], [unpaidDocuments?.documents?.length ?? 0])
  const { control, handleSubmit, setValue, errors } = useUnpaidDocumentsReminderForm(onSubmitHandler, { customer: defaultCustomer, unpaidDocumentIds })

  useEffect(() => {
    if (unpaidDocuments?.documents?.length) {
      setValue('unpaidDocumentIds', unpaidDocumentIds)
    }
  }, [unpaidDocuments?.documents?.length ?? 0])

  const submitButtonHandler = useMemo(() => handleSubmit(), [handleSubmit])

  return (
    <>
      <FormLayoutDrawer //
        open={open}
        onClose={onClose}
        title={t('pms.accounting.documents.send_reminder')}
        width={md ? '60%' : '100%'}
        toolbar={
          <FormLayoutDefaultToolbar //
            saveButton={{
              disabled: unpaidDocumentsLoading || sendEmailLoading,
              clickHandler: submitButtonHandler,
              text: t('pms.accounting.documents.send_reminder'),
              loading: unpaidDocumentsLoading || sendEmailLoading,
            }}
            cancelButton={{ clickHandler: () => onClose(), text: t('commons.close') }}
          />
        }
      >
        <form id="reminder">
          <Grid container rowSpacing={2}>
            <Grid item xs={12}>
              <HICTitleSmall>{t('pms.accounting.documents.owner_title')}</HICTitleSmall>
              <Controller<UnpaidDocumentsReminderFormSchema, 'customer'> //
                name="customer"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <DocumentCustomerInput //
                      referenceCustomers={[referenceCustomer]}
                      value={value}
                      onChange={(v) => {
                        onChange(v)
                        setLocalCustomerId(v.id)
                      }}
                      error={errors.customer}
                      customerCurrentData={null}
                      syncData={false}
                      dataSynched={() => {
                        console.log('synched')
                      }}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <HICTitleSmall>{t('pms.accounting.documents.unpaid_documnts_title')}</HICTitleSmall>
            </Grid>
            <Grid item xs={12}>
              <Controller<UnpaidDocumentsReminderFormSchema, 'unpaidDocumentIds'> //
                name="unpaidDocumentIds"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <UnpaidDocumentsSelectionGrid //
                      unpaidDocuments={unpaidDocuments?.documents ?? []}
                      onChange={field.onChange}
                      comeFromUnpaidDocumentId={comeFromUnpaidDocumentId}
                    />
                    <HICError>{fieldState.error?.message}</HICError>
                  </>
                )}
              />
            </Grid>
          </Grid>
        </form>
      </FormLayoutDrawer>
      <CustomerDetail
        appearance={{
          type: 'DIALOG',
          open: openEditCustomer,
          onClose: () => setOpenEditCustomer(false),
          maxWidth: '800px'
        }}
        customerId={referenceCustomer.id}
        onCancel={() => {
          setOpenEditCustomer(false)
        }}
        onUpserted={(customer) => {
          setValue('customer.email', customer.mailContacts?.find((mail) => mail.isDefault)?.value ?? '')
          setOpenEditCustomer(false)
        }}
        focusedField="EMAIL"
      />
    </>
  )
}
